import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import DateFormat from '../components/DateFormat';
import SubMenu from '../components/SubMenu';
import StageCreateModal from '../components/StageCreateModal';
import EditStageModal from '../components/EditStageModal';

const StageEquipmentListPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);


  useEffect(() => {
    fetchStages();
  }, []);


  const fetchStages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/inventory_crud.php?staging_list=1`);
      if (Array.isArray(res.data) && res.data[0] === 'success') {
        setStages(res.data[1]);
      } else {
        toast.error('Failed to load staging records');
      }
    } catch {
      toast.error('API error loading staging data!!');
    } finally {
      setLoading(false);
    }
  };

  const filteredStages = stages.filter(s =>
    (!search || s.stage_number.toLowerCase().includes(search.toLowerCase()) || s.job_number?.toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || s.status === statusFilter)
  );

  const getLinksForStage = (stage) => [
    { name: 'View Details', click: () => navigate(`/stage/${stage.id}`) },
    { name: 'Edit Stage', click: () => { setEditId(stage.id); setShowEdit(true); } },
  ];  

  const handleCreated = async (newId) => {
    // Optionally jump to detail:
    // if (newId) { navigate(`/stage/${newId}`); return; }

    try {
      await fetchStages();
      toast.success('Stage list updated');
    } catch {
      // fetchStages already toasts on error; this catch is defensive
    }
  };

  const handleEdited = async () => {
    await fetchStages();
    toast.success('Stage updated');
  };  

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-xl font-bold mb-4">Staging Equipment</h1>

      {/* Filter Bar */}
      <div className="mb-6 flex flex-col md:flex-row gap-3 w-full items-center bg-gray-50 rounded-md p-4 border">
        <button
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
          onClick={() => setShowCreate(true)}
        >
          + New Stage
        </button>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search Stage# or Job#"
          className="w-full md:w-2/5 border border-gray-300 rounded-md px-3 py-2"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 w-full md:w-auto"
        >
          <option value="">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Released">Released</option>
          <option value="Delivered">Delivered</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        <button
          onClick={() => {
            setSearch('');
            setStatusFilter('');
          }}
          className="bg-orange-500 hover:bg-orange-700 text-white px-4 py-2 rounded"
        >
          Reset
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="min-w-full border border-gray-200 rounded-lg">
          <thead className="bg-gray-200">
            <tr>
              <th className="border px-4 py-2">Stage#</th>
              <th className="border px-4 py-2">Job / Service Log</th>
              <th className="border px-4 py-2">Created By</th>
              <th className="border px-4 py-2">Created</th>
              <th className="border px-4 py-2">Status</th>
              <th className="border px-4 py-2">Notes</th>
              <th className="border px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStages.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-6 text-gray-400">No Staging Records Found.</td>
              </tr>
            ) : (
              filteredStages.map(stage => (
                <tr
                  key={stage.id}
                  className={`
                    hover:bg-indigo-50 hover:cursor-pointer
                    ${stage.status === 'Released' ? 'bg-yellow-100' : ''}
                    ${stage.status === 'Cancelled' ? 'bg-red-100 text-red-800' : ''}
                    ${stage.status === 'Delivered' ? 'bg-green-100 text-green-800' : ''}
                  `}
                >
                  <td className="border px-3 py-2 text-sm">{stage.id}</td>
                  <td className="border px-3 py-2 text-sm">{stage.job_number || '-'}</td>
                  <td className="border px-3 py-2 text-sm">{stage.created_by}</td>
                  <td className="border px-3 py-2 text-sm"><DateFormat date={stage.date_created} /></td>
                  <td className="border px-3 py-2 text-sm">{stage.status}</td>
                  <td className="border px-3 py-2 text-sm max-w-xs truncate">{stage.notes}</td>
                  <td className="border px-3 py-2 text-sm">
                    <SubMenu label="Actions" idItem={stage.id} links={getLinksForStage(stage)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    <StageCreateModal
      apiUrl={apiUrl}
      open={showCreate}
      onClose={() => setShowCreate(false)}
      onCreated={handleCreated}
    />

      {showEdit && editId && (
        <EditStageModal
          apiUrl={apiUrl}
          open={showEdit}
          stageId={editId}
          onClose={() => { setShowEdit(false); setEditId(null); }}
          onSaved={handleEdited}
        />
      )}
    </div>
  );
};

export default StageEquipmentListPage;
